/**
 * Installability and update handling.
 *
 * The service worker itself is generated by vite-plugin-pwa (see
 * vite.config.ts); this module owns everything the user actually sees:
 * the install button, the "new version" prompt, and the iOS fallback.
 *
 * Registration is deliberately `prompt` rather than `autoUpdate`. An
 * automatic reload would discard the county the user had drilled into and
 * restart the camera flight, so a new build waits behind a dismissible bar
 * instead.
 */
import { registerSW } from 'virtual:pwa-register'

/**
 * `beforeinstallprompt` is Chromium-only and not in lib.dom. Capturing the
 * event is the only way to trigger the install UI later — it cannot be
 * summoned on demand.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const isIos = (): boolean =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  // iPadOS 13+ reports itself as a Mac; the touch points give it away.
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

const isInstalled = (): boolean =>
  matchMedia('(display-mode: standalone)').matches ||
  matchMedia('(display-mode: window-controls-overlay)').matches ||
  // iOS Safari's own non-standard flag.
  (navigator as unknown as { standalone?: boolean }).standalone === true

/** One toast at a time, bottom-centre, above the timeline. */
function toast(message: string, action?: { label: string; run: () => void }): () => void {
  document.getElementById('pwa-toast')?.remove()

  const el = document.createElement('div')
  el.id = 'pwa-toast'
  el.className = 'pwa-toast'
  el.setAttribute('role', 'status')

  const text = document.createElement('span')
  text.className = 'pt-msg'
  text.textContent = message
  el.append(text)

  const dismiss = (): void => el.remove()

  if (action) {
    const btn = document.createElement('button')
    btn.className = 'pt-act'
    btn.textContent = action.label
    btn.addEventListener('click', () => {
      dismiss()
      action.run()
    })
    el.append(btn)
  }

  const close = document.createElement('button')
  close.className = 'pt-close'
  close.setAttribute('aria-label', 'Bezárás')
  close.textContent = '×'
  close.addEventListener('click', dismiss)
  el.append(close)

  document.body.append(el)
  return dismiss
}

export function initPwa(): void {
  const installBtn = document.getElementById('btn-install')

  // ---- updates ------------------------------------------------------------
  const updateSW = registerSW({
    onNeedRefresh() {
      toast('Új verzió érhető el.', { label: 'Frissítés', run: () => void updateSW(true) })
    },
    onOfflineReady() {
      const close = toast('Offline is használható.')
      setTimeout(close, 5000)
    },
  })

  // ---- install ------------------------------------------------------------
  if (!installBtn) return

  let deferred: BeforeInstallPromptEvent | null = null

  const show = (on: boolean): void => {
    installBtn.hidden = !on
  }

  addEventListener('beforeinstallprompt', (e) => {
    // Chromium shows its own mini-infobar unless this is prevented; we want
    // the install offer in the header, not over the map.
    e.preventDefault()
    deferred = e as BeforeInstallPromptEvent
    show(!isInstalled())
  })

  addEventListener('appinstalled', () => {
    deferred = null
    show(false)
  })

  // iOS never fires beforeinstallprompt and has no programmatic install, so
  // the button becomes a pointer to the Share-sheet route instead of hiding
  // the feature from half the mobile users.
  if (isIos() && !isInstalled()) show(true)

  installBtn.addEventListener('click', () => {
    if (deferred) {
      const prompt = deferred
      deferred = null
      void prompt.prompt().then(() =>
        prompt.userChoice.then(({ outcome }) => {
          if (outcome === 'accepted') show(false)
        }),
      )
      return
    }
    toast('Telepítés: Megosztás ikon → „Hozzáadás a kezdőképernyőhöz”.')
  })
}
